/**
 * SuspensionScreen — Pantalla de bloqueo total cuando la licencia está
 * en modo "blocked" (suspended / revoked / expired / invalid).
 *
 * Detecta dos escenarios y muestra mensajes diferenciados:
 *  1. plan_limit — client_count > max_clients (límite de plan excedido)
 *  2. admin_block — suspensión manual, expiración, revocación
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldX, ShieldAlert, LogOut, RefreshCw, Loader2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/auth";
import { apiCall } from "@/lib/api";
import { toast } from "sonner";

type LicenseStatus = {
  enabled: boolean;
  mode: string;
  message: string;
  plan: string | null;
  max_clients: number | null;
  client_count: number | null;
};

const PLAN_NAMES: Record<string, string> = {
  starter:    "Starter",
  basic:      "Basic",
  standard:   "Standard",
  pro:        "Pro",
  business:   "Business",
  enterprise: "Enterprise",
  unlimited:  "Unlimited",
};

export function SuspensionScreen() {
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();

  const { data: status } = useQuery<LicenseStatus>({
    queryKey: ["licensing-status"],
    queryFn: () => apiCall<LicenseStatus>("get", "/api/v1/licensing/status"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const heartbeatMut = useMutation({
    mutationFn: () => apiCall("post", "/api/v1/licensing/heartbeat"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["licensing-status"] });
      toast.success("Verificación enviada — recargando estado…");
    },
    onError: () => {
      toast.error("No se pudo verificar con ByteControl");
    },
  });

  // Detectar si el bloqueo es por límite de plan
  const isPlanLimit =
    status?.max_clients !== null &&
    status?.max_clients !== undefined &&
    status?.client_count !== null &&
    status?.client_count !== undefined &&
    status.client_count > status.max_clients;

  if (isPlanLimit) {
    return <PlanLimitScreen status={status!} heartbeatMut={heartbeatMut} logout={logout} />;
  }

  return <AdminBlockScreen status={status} heartbeatMut={heartbeatMut} logout={logout} />;
}

function PlanLimitScreen({
  status,
  heartbeatMut,
  logout,
}: {
  status: LicenseStatus;
  heartbeatMut: ReturnType<typeof useMutation>;
  logout: () => void;
}) {
  const planName = status.plan ? (PLAN_NAMES[status.plan] ?? status.plan) : "actual";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
      <div className="flex flex-col items-center gap-6 max-w-md w-full text-center">
        <div className="rounded-full bg-orange-100 dark:bg-orange-900/30 p-5">
          <TrendingUp size={48} className="text-orange-500" />
        </div>

        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold tracking-tight">Límite de plan excedido</h1>
          <p className="text-sm text-muted-foreground">
            Tu plan <strong>{planName}</strong> permite hasta <strong>{status.max_clients} contratos</strong>.
            Actualmente tienes <strong className="text-orange-500">{status.client_count}</strong> contratos activos.
          </p>
        </div>

        {/* Barra de progreso visual */}
        <div className="w-full">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Contratos activos</span>
            <span className="text-orange-500 font-medium">{status.client_count} / {status.max_clients}</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-orange-500 h-2 rounded-full"
              style={{ width: `${Math.min(100, ((status.client_count ?? 0) / (status.max_clients ?? 1)) * 100)}%` }}
            />
          </div>
        </div>

        <div className="rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 px-4 py-3 text-xs text-orange-800 dark:text-orange-300 text-left w-full">
          <p className="font-medium mb-1">¿Qué puedo hacer?</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Contacta a tu proveedor para actualizar al siguiente plan.</li>
            <li>O cancela contratos inactivos para liberar cupo en el plan actual.</li>
            <li>El acceso se restaura automáticamente al actualizar el plan en ByteControl.</li>
          </ul>
        </div>

        <div className="flex gap-3 w-full">
          <Button
            variant="outline"
            className="flex-1"
            disabled={heartbeatMut.isPending}
            onClick={() => (heartbeatMut as any).mutate()}
          >
            {heartbeatMut.isPending
              ? <Loader2 size={14} className="animate-spin mr-2" />
              : <RefreshCw size={14} className="mr-2" />
            }
            Verificar plan
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={() => { logout(); window.location.href = "/login"; }}
          >
            <LogOut size={14} className="mr-2" />
            Cerrar sesión
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">ByteISP — Actualiza tu plan para continuar</p>
      </div>
    </div>
  );
}

function AdminBlockScreen({
  status,
  heartbeatMut,
  logout,
}: {
  status: LicenseStatus | undefined;
  heartbeatMut: ReturnType<typeof useMutation>;
  logout: () => void;
}) {
  const mode = status?.mode ?? "blocked";

  const modeLabel: Record<string, string> = {
    blocked:    "Licencia bloqueada",
    no_license: "Sin licencia registrada",
  };
  const modeDesc: Record<string, string> = {
    blocked:    "Tu licencia de ByteISP ha sido suspendida, revocada o ha expirado. Contacta a tu proveedor para reactivarla.",
    no_license: "No hay ninguna licencia registrada en este sistema. Ve a Ajustes → Licencia para registrar tu clave.",
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
      <div className="flex flex-col items-center gap-6 max-w-md w-full text-center">
        <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-5">
          <ShieldX size={48} className="text-red-500" />
        </div>

        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {modeLabel[mode] ?? "Sistema bloqueado"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {status?.message ?? modeDesc[mode] ?? modeDesc.blocked}
          </p>
        </div>

        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-xs text-red-800 dark:text-red-300 text-left w-full">
          <p className="font-medium mb-1">¿Qué puedo hacer?</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Contacta a tu proveedor de ByteISP para verificar el estado de tu licencia.</li>
            <li>Si tienes una clave nueva, cierra sesión, vuelve a iniciar y ve a <strong>Ajustes → Licencia</strong>.</li>
            <li>Presiona "Verificar ahora" para reintentar la conexión con ByteControl.</li>
          </ul>
        </div>

        <div className="flex gap-3 w-full">
          <Button
            variant="outline"
            className="flex-1"
            disabled={heartbeatMut.isPending}
            onClick={() => (heartbeatMut as any).mutate()}
          >
            {heartbeatMut.isPending
              ? <Loader2 size={14} className="animate-spin mr-2" />
              : <RefreshCw size={14} className="mr-2" />
            }
            Verificar ahora
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={() => { logout(); window.location.href = "/login"; }}
          >
            <LogOut size={14} className="mr-2" />
            Cerrar sesión
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">ByteISP — Licencia requerida para operar</p>
      </div>
    </div>
  );
}
